/**
 * CSS object-position (p. ej. "42% 35%") para respetar el rectángulo focal de Wagtail
 * cuando la imagen se muestra con object-fit: cover.
 *
 * La API puede enviar cualquiera de estas formas:
 * - imagen_descripcion como objeto: { url, width, height, focal_point: { x, y, width, height } }
 * - imagen_descripcion_object_position: "42% 35%" (opcional, pasa tal cual)
 * - imagen_descripcion_focal: { x, y, width, height } en píxeles del original
 *   junto con imagen_descripcion_width e imagen_descripcion_height (natural)
 * - imagen_descripcion_meta: { width, height, focal: { x, y, width, height } }
 * - Campos planos: imagen_descripcion_focal_x|_y|_width|_height
 */

function readFocalRectFromImagen(imagen) {
  if (!imagen || typeof imagen !== "object") return null;
  const fp = imagen.focal_point ?? imagen.focal;
  if (!fp || typeof fp !== "object") return null;
  return {
    x: fp.x,
    y: fp.y,
    width: fp.width,
    height: fp.height,
  };
}

function readNaturalSizeFromImagen(imagen) {
  if (!imagen || typeof imagen !== "object") return null;
  const nw = imagen.width != null ? Number(imagen.width) : NaN;
  const nh = imagen.height != null ? Number(imagen.height) : NaN;
  if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw <= 0 || nh <= 0) {
    return null;
  }
  return { width: nw, height: nh };
}

function readFocalRect(volcan) {
  const fromImagen = readFocalRectFromImagen(volcan.imagen_descripcion);
  if (fromImagen) return fromImagen;

  const nested =
    volcan.imagen_descripcion_meta?.focal ??
    volcan.imagen_descripcion_meta?.focal_point;
  if (nested && typeof nested === "object") {
    return {
      x: nested.x,
      y: nested.y,
      width: nested.width,
      height: nested.height,
    };
  }
  const box = volcan.imagen_descripcion_focal;
  if (box && typeof box === "object") {
    return {
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
    };
  }
  if (
    volcan.imagen_descripcion_focal_x != null &&
    volcan.imagen_descripcion_focal_width != null
  ) {
    return {
      x: volcan.imagen_descripcion_focal_x,
      y: volcan.imagen_descripcion_focal_y,
      width: volcan.imagen_descripcion_focal_width,
      height: volcan.imagen_descripcion_focal_height,
    };
  }
  return null;
}

function readNaturalSize(volcan) {
  const fromImagen = readNaturalSizeFromImagen(volcan.imagen_descripcion);
  if (fromImagen) return fromImagen;

  const meta = volcan.imagen_descripcion_meta;
  const w =
    volcan.imagen_descripcion_width ??
    volcan.imagen_descripcion_image_width ??
    meta?.width;
  const h =
    volcan.imagen_descripcion_height ??
    volcan.imagen_descripcion_image_height ??
    meta?.height;
  const nw = w != null ? Number(w) : NaN;
  const nh = h != null ? Number(h) : NaN;
  if (!Number.isFinite(nw) || !Number.isFinite(nh) || nw <= 0 || nh <= 0) {
    return null;
  }
  return { width: nw, height: nh };
}

/**
 * @param {Record<string, unknown>} volcan
 * @returns {string|undefined} Valor para CSS object-position
 */
export function imagenDescripcionObjectPosition(volcan) {
  if (!volcan || typeof volcan !== "object") return undefined;

  const direct = volcan.imagen_descripcion_object_position;
  if (typeof direct === "string" && direct.trim()) {
    return direct.trim();
  }

  const size = readNaturalSize(volcan);
  const focal = readFocalRect(volcan);
  if (!size || !focal) return undefined;

  const x = Number(focal.x);
  const y = Number(focal.y);
  const fw = Number(focal.width);
  const fh = Number(focal.height);
  if ([x, y, fw, fh].some((v) => !Number.isFinite(v)) || fw <= 0 || fh <= 0) {
    return undefined;
  }

  const cx = ((x + fw / 2) / size.width) * 100;
  const cy = ((y + fh / 2) / size.height) * 100;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return undefined;

  const clamp = (v) => Math.max(0, Math.min(100, v));
  const px = clamp(cx).toFixed(2);
  const py = clamp(cy).toFixed(2);
  return `${px}% ${py}%`;
}

/**
 * Ruta o URL de la imagen de descripción (string legacy u objeto con url).
 * @param {unknown} imagenDescripcion
 * @returns {string}
 */
export function imagenDescripcionMediaPath(imagenDescripcion) {
  if (!imagenDescripcion) return "";
  if (typeof imagenDescripcion === "string") return imagenDescripcion;
  if (
    typeof imagenDescripcion === "object" &&
    typeof imagenDescripcion.url === "string"
  ) {
    return imagenDescripcion.url;
  }
  return "";
}
